import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { emitCoverageRefresh } from '@/features/codeCoverage/state/coverageRefreshSignal';
import { useOrganisationFilter } from '@/features/organisations/providers/organisationHooks';
import { useProjectFilter } from '@/features/projects/hooks/projectHooks';
import { deleteUpload, updateUploadTags } from '@/features/uploads/data';
import { useNotifications } from '@/providers/notificationHooks';
import { getToastMessage } from '@/shared/copy/toasts';
import { useApiErrorMessage } from '@/shared/data/errors';
import { updateTagColor, type TagColor } from '@/shared/data/tags';
import {
  buildCoverageUploadInvalidations,
  invalidateUploads,
  queryKeys,
  useInvalidateQueries,
} from '@/shared/hooks';

type DeleteTarget = {
  id: string;
  name: string;
  projectId: string;
};

type CoverageFileDeleteRow = {
  fileName: string;
  projectId: string;
  uploadId: string;
};

type CoverageFileTagRow = {
  id: string;
  projectId: string;
  tags: string[];
};

type UseCoverageFileTableRowActionsResult = {
  deleteLoading: boolean;
  deleteTarget: DeleteTarget | null;
  onDeleteClose: () => void;
  onDeleteConfirm: () => void;
  onDeleteRow: (row: CoverageFileDeleteRow) => void;
  onUpdateRowTagsColor: (input: {
    row: CoverageFileTagRow;
    tags: string[];
    colors: TagColor[];
  }) => void;
  updateRowTagsColorPendingId: string | null;
};

export const useCoverageFileTableRowActions = (): UseCoverageFileTableRowActionsResult => {
  const queryClient = useQueryClient();
  const invalidateQueries = useInvalidateQueries();
  const { selectedOrganisationId } = useOrganisationFilter();
  const { selectedProjectId } = useProjectFilter();
  const { notify } = useNotifications();
  const toErrorMessage = useApiErrorMessage();
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null);
  const [updatingTagRowId, setUpdatingTagRowId] = useState<string | null>(null);

  const deleteUploadMutation = useMutation({
    mutationFn: async (input: { id: string }) => {
      await deleteUpload(input, selectedOrganisationId);
      return input;
    },
    onSuccess: async (_deleted, input) => {
      await invalidateQueries([
        invalidateUploads(selectedOrganisationId ?? null, selectedProjectId ?? null),
        ...buildCoverageUploadInvalidations({
          selectedOrganisationId: selectedOrganisationId ?? null,
          selectedProjectId,
          uploadProjectId: deleteTarget?.projectId ?? null,
        }),
        {
          queryKey: queryKeys.uploadDetail(input.id, selectedOrganisationId ?? null),
          exact: false,
        },
      ]);
      emitCoverageRefresh();

      notify({
        message: getToastMessage('entity.deleted', {
          entity: 'coverage file',
          name: deleteTarget?.name ?? input.id,
        }),
        tone: 'green',
      });
      setDeleteTarget(null);
    },
    onError: (error) => {
      notify({ message: toErrorMessage(error), tone: 'red' });
    },
  });

  const updateTagColorMutation = useMutation({
    mutationFn: async (input: { tags: string[]; colors: TagColor[] }) => {
      const updates = input.tags
        .map((name, index) => ({ name: name.trim(), color: input.colors[index] ?? 'neutral' }))
        .filter((item) => item.name.length > 0);
      if (updates.length === 0) {
        return;
      }
      await Promise.all(
        updates.map(async (item) => {
          await updateTagColor(item, selectedOrganisationId ?? undefined);
        }),
      );
    },
  });

  const onDeleteRow = useCallback((row: CoverageFileDeleteRow) => {
    setDeleteTarget({
      id: row.uploadId,
      name: row.fileName.trim() || row.uploadId,
      projectId: row.projectId,
    });
  }, []);

  const onDeleteClose = useCallback(() => {
    if (deleteUploadMutation.isPending) {
      return;
    }
    setDeleteTarget(null);
  }, [deleteUploadMutation.isPending]);

  const onDeleteConfirm = useCallback(() => {
    if (!deleteTarget) {
      return;
    }
    deleteUploadMutation.mutate({ id: deleteTarget.id });
  }, [deleteTarget, deleteUploadMutation]);

  const onUpdateRowTagsColor = useCallback(
    ({ row, tags, colors }: { row: CoverageFileTagRow; tags: string[]; colors: TagColor[] }) => {
      const normalizedTags = Array.from(
        new Set(tags.map((tag) => tag.trim()).filter((tag) => tag.length > 0)),
      );
      const normalizedColors = colors.slice(0, normalizedTags.length);
      setUpdatingTagRowId(row.id);
      void (async () => {
        try {
          await updateUploadTags({ id: row.id, tags: normalizedTags }, selectedOrganisationId);
          if (normalizedTags.length > 0) {
            await updateTagColorMutation.mutateAsync({
              tags: normalizedTags,
              colors: normalizedColors,
            });
          }
          await invalidateQueries([
            invalidateUploads(selectedOrganisationId ?? null, selectedProjectId ?? null),
            ...buildCoverageUploadInvalidations({
              selectedOrganisationId: selectedOrganisationId ?? null,
              selectedProjectId,
              uploadProjectId: row.projectId,
            }),
            {
              queryKey: queryKeys.tagOptions(selectedProjectId, selectedOrganisationId ?? null),
              exact: false,
            },
          ]);
          await queryClient.refetchQueries({ type: 'active' });
          emitCoverageRefresh();
          notify({ message: getToastMessage('tags.updated'), tone: 'green' });
        } catch (error) {
          notify({ message: toErrorMessage(error), tone: 'red' });
        } finally {
          setUpdatingTagRowId(null);
        }
      })();
    },
    [
      invalidateQueries,
      notify,
      queryClient,
      selectedOrganisationId,
      selectedProjectId,
      toErrorMessage,
      updateTagColorMutation,
    ],
  );

  return {
    deleteLoading: deleteUploadMutation.isPending,
    deleteTarget,
    onDeleteClose,
    onDeleteConfirm,
    onDeleteRow,
    onUpdateRowTagsColor,
    updateRowTagsColorPendingId: updatingTagRowId,
  };
};
